import {Injectable, PLATFORM_ID, Inject, inject} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {AiSettingsInterface, DEFAULT_TOOLS_AI_SETTINGS} from '@llm/interfaces/settings-ai';
import {Tools_aiConsoleService} from '@llm/service/tools_ai-console.service';
import {llmStoreService} from '@llm/service/llm_store.service';
import {DataService} from '@serv/data.service';
import {environment} from '@env/environment';

/**
 * @Injectable
 * @description
 * Servicio singleton encargado de gestionar la persistencia de la configuración
 * de la IA del chatbot en el `localStorage` del navegador.
 *
 * @Responsabilidades
 * - Cargar la configuración desde `localStorage` al iniciar la aplicación.
 * - Sincronizar el estado de la configuración en el `llmStoreService` (el store de signals).
 * - Guardar los cambios de configuración en `localStorage` y en el store.
 * - Limpiar la configuración de `localStorage` y resetear el estado en el store.
 * - Funciona de forma segura tanto en el navegador como en el servidor (SSR).
 */
@Injectable({
  providedIn: 'root'
})
export class ToolsAiStorageService {
  /**
   * @private
   * @readonly
   * @description Clave única utilizada para almacenar y recuperar la configuración
   * del `localStorage`. Es una buena práctica usar una clave específica y constante.
   */
  private readonly SETTINGS_KEY = 'tecinco-web-MekanicGo-configuracoes-tool-ai-gloria';
  /**
   * @private
   * @description Flag booleano que indica si el código se está ejecutando en un
   * entorno de navegador. Es crucial para evitar errores al intentar acceder a
   * `localStorage` durante el Renderizado en el Lado del Servidor (SSR).
   */
  private isBrowser: boolean;
  /**
   * @private
   * @description Inyección del servicio de consola para registrar mensajes de error.
   */
  private _console = inject(Tools_aiConsoleService);
  /**
   * @private
   * @description Inyección del servicio de store (`signals`) para gestionar el estado reactivo.
   */
  private _store = inject(llmStoreService);

  private _dataService = inject(DataService);


  private default_tools_ai_settings: AiSettingsInterface = {
    ...DEFAULT_TOOLS_AI_SETTINGS,
    username: this._dataService.user.firstName
  };

  /**
   * @constructor
   * @description El constructor se ejecuta una sola vez cuando se crea la instancia del servicio.
   * Realiza la inicialización crítica:
   * 1. Determina si está en un entorno de navegador.
   * 2. Carga las configuraciones guardadas desde `localStorage`.
   * 3. Establece el estado inicial en el `llmStoreService` con los datos cargados o los valores por defecto.
   * @param {object} platformId - Token de inyección de Angular para identificar la plataforma actual.
   */
  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    const savedSettings = this.loadSettingsFromLocalStorage();
    this._store.setSettingsToolsAi(savedSettings || this.default_tools_ai_settings);
  }

  /**
   * @method saveSettingsAi
   * @description Guarda un nuevo objeto de configuración. Actualiza el estado en el store (signal)
   * para una reactividad inmediata en la UI y luego persiste los datos en `localStorage`
   * para futuras sesiones.
   * @param {AiSettingsInterface} settings - El objeto de configuración completo a guardar.
   * @returns {void}
   */
  saveSettingsAi(settings: AiSettingsInterface): void {
    if (environment.production) {
      settings = {...settings, consoleEnabled: false};
    }
    this._store.setSettingsToolsAi(settings);
    if (this.isBrowser) {
      localStorage.setItem(this.SETTINGS_KEY, JSON.stringify(settings));
    }
  }

  /**
   * @private
   * @method loadSettingsFromLocalStorage
   * @description Método auxiliar interno para leer y parsear de forma segura la configuración
   * desde `localStorage`. Maneja el caso de que los datos no existan o estén corruptos.
   * @returns {AiSettingsInterface | null} El objeto de configuración o `null`.
   */
  private loadSettingsFromLocalStorage(): AiSettingsInterface | null {
    if (this.isBrowser) {
      const settingsString = localStorage.getItem(this.SETTINGS_KEY);
      if (settingsString) {
        try {
          return JSON.parse(settingsString) as AiSettingsInterface;
        } catch (e) {
          this._console.error('Erro ao analisar as configurações do localStorage:', e);
          this.clearSettings();
          return this.default_tools_ai_settings;
        }
      }
    }
    return null;
  }

  /**
   * @method clearSettings
   * @description Resetea la configuración. Actualiza el estado en el store a los valores
   * por defecto y elimina la entrada correspondiente del `localStorage`.
   * @returns {void}
   */
  clearSettings(): void {
    this._store.setSettingsToolsAi(this.default_tools_ai_settings);
    if (this.isBrowser) {
      localStorage.removeItem(this.SETTINGS_KEY);
      localStorage.setItem(this.SETTINGS_KEY, JSON.stringify(this.default_tools_ai_settings));

    }
  }


}
